import useNotifier from "@dashboard/hooks/useNotifier";
import { createFetch } from "@saleor/sdk";
import { useIntl } from "react-intl";

/**
 * Downloads a template XLSX from the backend REST endpoint.
 *
 * Saleor stores the JWT access token in-memory inside the @saleor/sdk module
 * singleton (not in localStorage — only the refresh token is persisted there).
 * `createFetch()` from the SDK returns a fetch wrapper that automatically
 * reads from that singleton and injects the Authorization header, exactly
 * as Apollo does for GraphQL requests.  The singleton is initialised by
 * `createSaleorClient()` in src/graphql/client.ts at app startup, so calling
 * `createFetch()` here (at module scope, once) is safe.
 */
const sdkFetch = createFetch();

export function useTemplateDownload() {
  const notify = useNotifier();
  const intl = useIntl();

  const download = async (url: string, filename: string) => {
    try {
      const response = await sdkFetch(url, {
        method: "GET",
        headers: {
          Accept: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, */*",
        },
        credentials: "include",
      } as RequestInit);

      if (!response.ok) {
        notify({
          status: "error",
          text: intl.formatMessage(
            {
              id: "qf8P8V",
              defaultMessage: "Failed to download template ({status})",
            },
            { status: response.status },
          ),
        });

        return;
      }

      const blob = await response.blob();
      const objectUrl = URL.createObjectURL(blob);
      const anchor = document.createElement("a");

      anchor.href = objectUrl;
      anchor.download = filename;
      document.body.appendChild(anchor);
      anchor.click();
      document.body.removeChild(anchor);
      URL.revokeObjectURL(objectUrl);
    } catch {
      // Network / CORS error — open in a new tab as a last-resort fallback
      window.open(url, "_blank", "noreferrer");
    }
  };

  return { download };
}
